<template>
  <div v-if="show" class="integration-expired-banner">
    <div class="integration-expired-banner__message" v-html="message"></div>
    <button
      type="button"
      class="integration-expired-banner__close"
      aria-label="Dismiss alert"
      @click="dismiss">
      <img
        src="../../assets/img/icn_close.svg"
        alt=""
        class="document-preview-close-icon"
        aria-hidden="true">
    </button>
  </div>
</template>

<script>
import moment from 'moment'

const SHOW_XERO_EXPIRED_BANNER = false
const SHOW_SERVICEM8_EXPIRED_BANNER = true

export default {
  name: 'IntegrationExpiredBanner',
  data: function () {
    return {
      userDismissed: false
    }
  },
  computed: {
    alertActive: function () {
      return this.isXeroExpired || this.isServiceM8Expired
    },
    show: function () {
      if (!this.$store.getters['getHeader']) return false
      if (!this.$store.getters['user/isAuthenticated']) return false
      if (!this.alertActive) return false
      return !this.userDismissed
    },
    isServiceM8Expired: function () {
      if (!SHOW_SERVICEM8_EXPIRED_BANNER) return false
      const integrations = this.$store.getters['company/getIntegrations']
      if (!integrations || typeof integrations !== 'object' || Array.isArray(integrations)) {
        return false
      }
      // Only when refresh failed (or refresh token itself is invalid).
      // Do NOT key off service_m8_expires_at — SM8 access tokens expire ~hourly;
      // that is normal and refreshed silently. Refresh tokens last ~1 year.
      return integrations.service_m8_status === 'error'
    },
    isXeroExpired: function () {
      if (!SHOW_XERO_EXPIRED_BANNER) return false
      const integrations = this.$store.getters['company/getIntegrations']
      if (!integrations || !integrations.xero_expires_at) return false
      return moment(integrations.xero_expires_at).isBefore(moment())
    },
    message: function () {
      const parts = []
      if (SHOW_SERVICEM8_EXPIRED_BANNER && this.isServiceM8Expired) {
        parts.push('<strong>ServiceM8</strong> connection needs to be renewed. Re-authorise ServiceM8 to enable syncing.')
      }
      if (SHOW_XERO_EXPIRED_BANNER && this.isXeroExpired) {
        parts.push('<strong>Xero</strong> access token has expired due to inactivity. Re-authorise Xero to enable syncing.')
      }
      return parts.join('<br/>')
    }
  },
  watch: {
    alertActive: function (active) {
      // Load dismiss preference when the alert becomes relevant.
      // Do NOT clear dismiss when alert goes inactive — logout / empty
      // integrations briefly clear alertActive and would wipe dismiss,
      // so the banner reappears on every login while status stays error.
      if (active) this.loadDismissStorage()
    },
    // Clear dismiss only once SM8 is healthy again so a future real
    // failure can show the banner.
    '$store.getters["company/getIntegrations"]': function (integrations) {
      if (integrations && integrations.service_m8_status === 'connected') {
        this.userDismissed = false
        this.clearDismissStorage()
      }
    }
  },
  mounted: function () {
    this.loadDismissStorage()
  },
  methods: {
    dismissStorageKey: function () {
      const companyId = (this.$store.getters['user/getProfile'] || {}).company_id || 0
      return 'integration_expired_banner_dismissed_' + companyId
    },
    loadDismissStorage: function () {
      try {
        this.userDismissed = localStorage.getItem(this.dismissStorageKey()) === '1'
      } catch (e) {
        this.userDismissed = false
      }
    },
    clearDismissStorage: function () {
      try {
        localStorage.removeItem(this.dismissStorageKey())
      } catch (e) { /* ignore */ }
    },
    dismiss: function () {
      this.userDismissed = true
      try {
        localStorage.setItem(this.dismissStorageKey(), '1')
      } catch (e) { /* ignore */ }
    }
  }
}
</script>

<style scoped>
.integration-expired-banner {
  width: 100%;
  margin-top: 48px;
  padding: 8px 3rem 8px 1rem;
  background-color: var(--color-error);
  color: #fff;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.4;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.integration-expired-banner__message {
  flex: 1;
  text-align: center;
}
.integration-expired-banner__close {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  min-width: 44px;
  -webkit-tap-highlight-color: transparent;
}
.integration-expired-banner__close:hover .document-preview-close-icon {
  opacity: 1;
}
.integration-expired-banner__close:focus {
  outline: none;
}
.integration-expired-banner__close .document-preview-close-icon {
  width: 16px;
  height: 16px;
  filter: brightness(0) invert(1);
  opacity: 0.85;
}
</style>
